import { useState, useMemo } from 'react';
// eslint-disable-next-line no-unused-vars
import { motion, AnimatePresence } from 'framer-motion';
import { Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { OnSiteEmployeeRow } from './OnSiteEmployeeRow';

function getDisplayName(checkIn) {
  if (checkIn.display_name) return checkIn.display_name;
  const parts = [checkIn.first_name, checkIn.last_name].filter(Boolean);
  return parts.join(' ') || 'Unknown';
}

export function LiveOnSitePanel({
  onSiteUsers = [],
  getEmployeeStats,
  statsLoading,
  onCheckOut,
  onViewSelfie,
}) {
  const [sortBy, setSortBy] = useState('clockIn');

  const sortedUsers = useMemo(() => {
    const users = [...onSiteUsers];

    switch (sortBy) {
      case 'name':
        users.sort((a, b) => {
          const nameA = getDisplayName(a).toLowerCase();
          const nameB = getDisplayName(b).toLowerCase();
          return nameA.localeCompare(nameB);
        });
        break;
      case 'duration':
        users.sort((a, b) => {
          const timeA = new Date(a.check_in_time || 0).getTime();
          const timeB = new Date(b.check_in_time || 0).getTime();
          return timeA - timeB;
        });
        break;
      case 'clockIn':
      default:
        users.sort((a, b) => {
          const timeA = new Date(a.check_in_time || 0).getTime();
          const timeB = new Date(b.check_in_time || 0).getTime();
          return timeB - timeA;
        });
        break;
    }

    return users;
  }, [onSiteUsers, sortBy]);

  return (
    <div className="flex-1 flex flex-col bg-white rounded-xl border border-border overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
        </span>
        <h3 className="text-sm font-semibold text-foreground">Live On-Site</h3>
        <Badge variant="secondary" className="text-xs h-5 px-1.5">
          {onSiteUsers.length}
        </Badge>
        <div className="ml-auto">
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="h-8 text-xs w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="clockIn">Recent Clock-In</SelectItem>
              <SelectItem value="name">Name A-Z</SelectItem>
              <SelectItem value="duration">Longest On-Site</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Body */}
      {sortedUsers.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center py-12 gap-2">
          <Users className="h-12 w-12 text-muted-foreground opacity-30" />
          <p className="text-sm font-medium text-muted-foreground">No one on-site</p>
          <p className="text-xs text-muted-foreground">
            Employees will appear here when they clock in
          </p>
        </div>
      ) : (
        <ScrollArea className="flex-1">
          <AnimatePresence mode="popLayout">
            {sortedUsers.map((checkIn) => (
              <motion.div
                key={checkIn.id}
                layout
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 12 }}
                transition={{ duration: 0.2 }}
              >
                <OnSiteEmployeeRow
                  checkIn={checkIn}
                  employeeStats={getEmployeeStats?.(checkIn)}
                  statsLoading={statsLoading}
                  onCheckOut={onCheckOut}
                  onViewSelfie={onViewSelfie}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </ScrollArea>
      )}
    </div>
  );
}
